// Shared board model for Playtest and Duel: types, pure state transforms, and
// the position-slice geometry. No React/JSX here — see board/components.tsx.
import type { CSSProperties } from 'react';
import type { Modifier } from '@dnd-kit/core';
import type { Deck, DeckCard } from '../../../shared/types';

export type Pos = 'atk' | 'def';
// `plant`: a card shuffled face-up into a deck (revealed while it's the top card,
// and auto-revealed + haloed when drawn).
export interface Inst { uid: string; name: string; plant?: boolean; }
// atk/def are overrides of the card's printed stats (kept while on the field).
export interface Placed extends Inst { faceUp: boolean; pos: Pos; counters?: number; atk?: number; def?: number; }
export interface Board {
  deck: Inst[]; hand: Inst[];
  monsters: (Placed | null)[]; spells: (Placed | null)[]; fieldSpell: Placed | null;
  gy: Inst[]; extra: Inst[]; banished: Inst[]; skill: Placed | null;
}

// Dice/coin lifecycle: idle → rolling (building up) → cooldown (result shown,
// briefly locked so it's seen) → idle.
export type SpinState = 'idle' | 'rolling' | 'cooldown';

let UID = 0;
export const nextUid = () => `c${++UID}`;

// Fixed drag-overlay size (must match .play-card.drag-overlay in index.css). We
// center on this box — not the source node — so the card stays under the cursor
// regardless of where it was grabbed, and its centre is unchanged when it
// rotates to defense (rotation pivots on the same centre).
export const OVERLAY_W = 88;
export const OVERLAY_H = 128;

// Center the drag overlay on the pointer so the drop is decided by the card's
// middle (not wherever it was grabbed).
export const snapCenterToCursor: Modifier = ({ activatorEvent, active, draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !activatorEvent) return transform;
  const ev = activatorEvent as MouseEvent & TouchEvent;
  const x = ev.touches?.[0]?.clientX ?? ev.clientX ?? 0;
  const y = ev.touches?.[0]?.clientY ?? ev.clientY ?? 0;
  // The counter overlay is a small chip, not a card — center on its own size.
  const kind = active?.data?.current?.kind as string | undefined;
  const counter = kind === 'counter-add' || kind === 'counter-move';
  const w = counter ? 20 : OVERLAY_W;
  const h = counter ? 20 : OVERLAY_H;
  return {
    ...transform,
    x: transform.x + (x - draggingNodeRect.left) - w / 2,
    y: transform.y + (y - draggingNodeRect.top) - h / 2,
  };
};

export function shuffle<T>(a: T[]): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [r[i], r[j]] = [r[j], r[i]]; }
  return r;
}
export const expand = (list: DeckCard[]): Inst[] =>
  list.flatMap(c => Array.from({ length: c.count }, () => ({ uid: nextUid(), name: c.name })));

export function buildBoard(deck: Deck): Board {
  const main = shuffle(expand(deck.mainDeck));
  const hand = main.splice(0, 4);
  return {
    deck: main, hand,
    monsters: [null, null, null], spells: [null, null, null], fieldSpell: null,
    gy: [], extra: expand(deck.extraDeck), banished: [],
    skill: deck.skill ? { uid: 'skill', name: deck.skill, faceUp: true, pos: 'atk' } : null,
  };
}

/** Build a board from a deck using a server-supplied opening permutation of the
 *  main deck (indices). Falls back to a local shuffle if the perm doesn't fit. */
export function buildBoardWithPerm(deck: Deck, perm: number[]): Board {
  const main = expand(deck.mainDeck);
  const ordered = perm.length === main.length ? perm.map(i => main[i]) : shuffle(main);
  const deckArr = [...ordered];
  const hand = deckArr.splice(0, 4);
  return {
    deck: deckArr, hand,
    monsters: [null, null, null], spells: [null, null, null], fieldSpell: null,
    gy: [], extra: expand(deck.extraDeck), banished: [],
    skill: deck.skill ? { uid: 'skill', name: deck.skill, faceUp: true, pos: 'atk' } : null,
  };
}

/** Reorder a pile by a server permutation (used for authoritative reshuffles). */
export function applyPerm<T>(items: T[], perm: number[]): T[] {
  if (perm.length !== items.length) return shuffle(items);
  return perm.map(i => items[i]);
}

/** The redacted view of a board shown to the OPPONENT: hand/deck/extra become
 *  anonymous counts and face-down field cards lose their identity. Graveyard,
 *  banished, skill, and face-up cards are public. Never leaks hidden names. */
export function publicView(b: Board): Board {
  const anon = (c: Inst): Inst => ({ uid: c.uid, name: '' });
  const redact = (p: Placed | null): Placed | null => (p ? (p.faceUp ? p : { ...p, name: '' }) : null);
  return {
    // A face-up "plant" on top of the deck is public; every other deck card is hidden.
    deck: b.deck.map((c, i) => (i === 0 && c.plant ? { uid: c.uid, name: c.name, plant: true } : anon(c))),
    hand: b.hand.map(anon),
    extra: b.extra.map(anon),
    gy: b.gy,
    banished: b.banished,
    monsters: b.monsters.map(redact),
    spells: b.spells.map(redact),
    fieldSpell: redact(b.fieldSpell),
    skill: b.skill,
  };
}

// ── pure board transforms ──────────────────────────────────────────────────
export const isFieldZone = (z: string) => z === 'fieldspell' || z === 'skill' || z.startsWith('mon-') || z.startsWith('st-');
// Counters are only allowed on cards in monster, spell/trap, and field-spell zones.
export const isCounterZone = (z: string) => z === 'fieldspell' || z.startsWith('mon-') || z.startsWith('st-');
export function zoneCard(b: Board, zone: string): Placed | null {
  if (zone === 'skill') return b.skill;
  if (zone === 'fieldspell') return b.fieldSpell;
  if (zone.startsWith('mon-')) return b.monsters[+zone.slice(4)];
  if (zone.startsWith('st-')) return b.spells[+zone.slice(3)];
  return null;
}
export function removeFrom(b: Board, src: string, uid: string): [Board, Inst | null] {
  const pull = (arr: Inst[]): [Inst[], Inst | null] => {
    const inst = arr.find(c => c.uid === uid) ?? null;
    return [arr.filter(c => c.uid !== uid), inst && { uid: inst.uid, name: inst.name }];
  };
  if (src === 'hand') { const [hand, inst] = pull(b.hand); return [{ ...b, hand }, inst]; }
  if (src === 'deck') { const [deck, inst] = pull(b.deck); return [{ ...b, deck }, inst]; }
  if (src === 'gy') { const [gy, inst] = pull(b.gy); return [{ ...b, gy }, inst]; }
  if (src === 'extra') { const [extra, inst] = pull(b.extra); return [{ ...b, extra }, inst]; }
  if (src === 'banished') { const [banished, inst] = pull(b.banished); return [{ ...b, banished }, inst]; }
  if (src === 'skill') { const p = b.skill; return [{ ...b, skill: null }, p && { uid: p.uid, name: p.name }]; }
  if (src === 'fieldspell') { const p = b.fieldSpell; return [{ ...b, fieldSpell: null }, p && { uid: p.uid, name: p.name }]; }
  if (src.startsWith('mon-')) { const i = +src.slice(4); const p = b.monsters[i]; const m = [...b.monsters]; m[i] = null; return [{ ...b, monsters: m }, p && { uid: p.uid, name: p.name }]; }
  if (src.startsWith('st-')) { const i = +src.slice(3); const p = b.spells[i]; const s = [...b.spells]; s[i] = null; return [{ ...b, spells: s }, p && { uid: p.uid, name: p.name }]; }
  return [b, null];
}
export function putZone(b: Board, dest: string, card: Placed): Board {
  if (dest === 'skill') return { ...b, skill: card };
  if (dest === 'fieldspell') return { ...b, fieldSpell: card };
  if (dest.startsWith('mon-')) { const i = +dest.slice(4); const m = [...b.monsters]; m[i] = card; return { ...b, monsters: m }; }
  if (dest.startsWith('st-')) { const i = +dest.slice(3); const s = [...b.spells]; s[i] = card; return { ...b, spells: s }; }
  return b;
}
export function putPile(b: Board, dest: string, inst: Inst): Board {
  if (dest === 'hand') return { ...b, hand: [...b.hand, inst] };
  if (dest === 'gy') return { ...b, gy: [...b.gy, inst] };
  if (dest === 'extra') return { ...b, extra: [...b.extra, inst] };
  if (dest === 'banished') return { ...b, banished: [...b.banished, inst] };
  if (dest === 'deck') return { ...b, deck: [...b.deck, inst] };
  return b;
}

// ── position slices ─────────────────────────────────────────────────────────
export interface PosDef { faceUp: boolean; pos: Pos; label: string; short: string; }
// Monster zones split into 4 triangular slices by the zone's two diagonals:
//   top = face-up ATK, bottom = face-down ATK, left = face-up DEF, right = face-down DEF.
export const MON_SLICES: PosDef[] = [
  { faceUp: true, pos: 'atk', label: 'Attack', short: 'ATK' },        // 0 top
  { faceUp: false, pos: 'atk', label: 'Face-Down', short: 'Set' },    // 1 bottom
  { faceUp: true, pos: 'def', label: 'Defense', short: 'DEF' },       // 2 left
  { faceUp: false, pos: 'def', label: 'Set Defense', short: 'Set DEF' }, // 3 right
];
// Field-spell / spell-trap / skill are always upright — 2 halves only:
//   top = face-up, bottom = face-down. No horizontal (defense) orientation.
export const FLAT_SLICES: PosDef[] = [
  { faceUp: true, pos: 'atk', label: 'Face-Up', short: 'Face-Up' },   // 0 top
  { faceUp: false, pos: 'atk', label: 'Set (face-down)', short: 'Set' }, // 1 bottom
];
/** Strips the `opp-` prefix duel opponent zones carry so shared zone helpers
 *  (slice chooser, position scheme) treat them like the equivalent own-zone. */
export const baseZone = (z: string) => (z.startsWith('opp-') ? z.slice(4) : z);
export const isOppZone = (z: string) => z.startsWith('opp-');
/** A field zone (own OR opponent's) that takes a placed card with a position. */
export const isAnyFieldZone = (z: string) => isFieldZone(baseZone(z));

export const slicesFor = (zone: string): PosDef[] => (baseZone(zone).startsWith('mon-') ? MON_SLICES : FLAT_SLICES);

/** Which position slice of a zone a pointer is over. Monster zones use the two
 *  diagonals (top/bottom/left/right triangles); other zones use top/bottom halves. */
export function sliceAt(zoneid: string, x: number, y: number): number {
  const el = document.querySelector(`[data-zoneid="${zoneid}"]`);
  if (!el) return 0;
  const r = el.getBoundingClientRect();
  // Normalize to [-1,1] so the split follows the zone's true corner-to-corner diagonals.
  const dx = (x - (r.left + r.width / 2)) / (r.width / 2);
  const dy = (y - (r.top + r.height / 2)) / (r.height / 2);
  const bz = baseZone(zoneid);
  if (bz === 'deck') {
    // Top / Bottom halves on the left, plus a right-triangle "Shuffle" slice.
    if (dx > 0 && Math.abs(dx) > Math.abs(dy)) return 2;      // right triangle → shuffle in
    return dy < 0 ? 0 : 1;                                    // top : bottom
  }
  if (bz.startsWith('mon-')) {
    if (Math.abs(dx) > Math.abs(dy)) return dx < 0 ? 2 : 3;   // left : right
    return dy < 0 ? 0 : 1;                                    // top : bottom
  }
  return dy < 0 ? 0 : 1;                                      // top : bottom half
}

// Triangle/half clip-paths + label alignment for each slice, per zone type.
export const MON_CLIPS = [
  'polygon(0 0, 100% 0, 50% 50%)',       // top
  'polygon(0 100%, 100% 100%, 50% 50%)', // bottom
  'polygon(0 0, 0 100%, 50% 50%)',       // left
  'polygon(100% 0, 100% 100%, 50% 50%)', // right
];
export const FLAT_CLIPS = ['inset(0 0 50% 0)', 'inset(50% 0 0 0)']; // top / bottom halves
export const MON_ALIGN: CSSProperties[] = [
  { alignItems: 'flex-start', justifyContent: 'center', paddingTop: 3 },
  { alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 3 },
  { alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 2 },
  { alignItems: 'center', justifyContent: 'flex-end', paddingRight: 2 },
];
export const FLAT_ALIGN: CSSProperties[] = [
  { alignItems: 'flex-start', justifyContent: 'center', paddingTop: 5 },
  { alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 5 },
];
// Deck: Top / Bottom on the left, Shuffle as the right triangle (matches the
// monster zone's right slice).
export const DECK_CLIPS = [
  'polygon(0 0, 100% 0, 50% 50%, 0 50%)',       // top-left region → Top
  'polygon(0 100%, 100% 100%, 50% 50%, 0 50%)', // bottom-left region → Bottom
  'polygon(100% 0, 100% 100%, 50% 50%)',        // right triangle → Shuffle
];
export const DECK_ALIGN: CSSProperties[] = [
  { alignItems: 'flex-start', justifyContent: 'center', paddingTop: 4 },
  { alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 4 },
  { alignItems: 'center', justifyContent: 'flex-end', paddingRight: 3 },
];

/** Slice labels + clip-paths + alignment for a zone: 4 triangles for monster
 *  zones, Top/Bottom halves for the deck, face-up/down halves otherwise. */
export function sliceConfig(zone: string): { labels: string[]; clips: string[]; align: CSSProperties[] } {
  const z = baseZone(zone);
  if (z === 'deck') return { labels: ['Top', 'Bottom', 'Shuffle'], clips: DECK_CLIPS, align: DECK_ALIGN };
  if (z.startsWith('mon-')) return { labels: MON_SLICES.map(s => s.short), clips: MON_CLIPS, align: MON_ALIGN };
  return { labels: FLAT_SLICES.map(s => s.short), clips: FLAT_CLIPS, align: FLAT_ALIGN };
}

/** Position a popup centered on a zone (clamped to the viewport). */
export function anchorStyle(zoneid: string | null): CSSProperties | undefined {
  if (!zoneid) return undefined;
  const el = document.querySelector(`[data-zoneid="${zoneid}"]`);
  if (!el) return undefined;
  const r = el.getBoundingClientRect();
  const x = Math.min(Math.max(r.left + r.width / 2, 160), window.innerWidth - 160);
  const y = Math.min(Math.max(r.top + r.height / 2, 170), window.innerHeight - 170);
  return { position: 'fixed', left: x, top: y, transform: 'translate(-50%, -50%)', margin: 0 };
}
